import { EXPERIENCE_NOTE_STARS } from '@matter_extractor/shared';
import type { ExperienceBlockDraft, ExperienceVariable } from '@matter_extractor/shared';
import { type ReactNode, useId, useState } from 'react';
import { READER_TEST_IDS, type ReaderMode } from '../contracts';
import { readerNote } from '../reader-play';
import { useRadioGroupKeys } from './radio-group';
import { VariableText } from './variable-text';

export interface NoteBlockProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'note' }>;
  readonly mode: ReaderMode;
  /** The note given, as text - « 4 » - or the empty string. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly resolveVariable: ((variable: ExperienceVariable) => string | null) | undefined;
  /** A preview NAMES its variables rather than resolving them (D4, D5) - see `reader-text.ts`. */
  readonly variableNames: ((variable: ExperienceVariable) => string) | undefined;
}

const STARS = Array.from({ length: EXPERIENCE_NOTE_STARS }, (_, index) => index + 1);

/** « 1 étoile sur 5 » to « 5 étoiles sur 5 »: what a screen reader says of one star (§4.2). */
function starName(star: number): string {
  return `${star} ${star === 1 ? 'étoile' : 'étoiles'} sur ${EXPERIENCE_NOTE_STARS}`;
}

/**
 * The Note block (retour-experience.md §4.2; issue #176): a question and five
 * stars in a row.
 *
 * ## What the stars show
 *
 * The note GIVEN is full, up to its star. The mouse, over a star, lights the
 * stars up to the one aimed - but those BEYOND the note given are drawn paler
 * (`data-state="hover"`), « pour qu'elle reste lisible » (revue du 2026-10-06):
 * the note given never changes under the pointer, only what a click would add.
 * A touch has no hover, and the question does not arise.
 *
 * ## The value is the digit
 *
 * « 4 », as text - comparable from one session to the next. A second click on
 * the star of the note given turns everything off, unless the block is
 * required (the rule of the Sélection).
 *
 * ## A radiogroup of five values
 *
 * Each star is a `<button role="radio">` named « N étoile(s) sur 5 », and only
 * the star of the note given is `aria-checked`: the stars below it are lit, not
 * chosen. The keys are `useRadioGroupKeys`'s.
 */
export function NoteBlock({
  block,
  mode,
  value,
  onChange,
  resolveVariable,
  variableNames,
}: NoteBlockProps): ReactNode {
  const labelId = useId();
  const interactive = mode === 'interactive';
  const given = readerNote(value);
  const [aimed, setAimed] = useState(0);
  const keys = useRadioGroupKeys(EXPERIENCE_NOTE_STARS, given === 0 ? null : given - 1);

  const pick = (star: number): void => {
    if (!interactive) return;
    if (star === given) {
      if (!block.required) onChange('');
      return;
    }
    onChange(String(star));
  };

  const stateOf = (star: number): 'on' | 'hover' | undefined => {
    if (star <= given) return 'on';
    return interactive && star <= aimed ? 'hover' : undefined;
  };

  return (
    <div className="lecteur-champ">
      <p className="lecteur-etiquette" id={labelId}>
        <VariableText
          text={block.question}
          resolveVariable={resolveVariable}
          variableNames={variableNames}
        />
      </p>
      <div
        className="lecteur-note"
        data-testid={READER_TEST_IDS.note}
        role="radiogroup"
        aria-labelledby={labelId}
        aria-required={block.required}
        onKeyDown={keys.onKeyDown}
        onPointerLeave={() => setAimed(0)}
      >
        {STARS.map((star) => (
          <button
            key={star}
            type="button"
            // biome-ignore lint/a11y/useSemanticElements: the APG radio group - a `<button role="radio">` - is what lets a star be drawn as one and be turned off by a second click, which a native radio cannot
            role="radio"
            className="lecteur-etoile"
            data-testid={READER_TEST_IDS.noteStar}
            data-state={stateOf(star)}
            aria-checked={star === given}
            aria-label={starName(star)}
            tabIndex={keys.tabIndexOf(star - 1)}
            onFocus={keys.onFocusOf(star - 1)}
            onPointerEnter={(event) => {
              // A touch has no hover: its pointer « enters » on the tap and would
              // leave the stars aimed until the next one.
              if (event.pointerType !== 'touch') setAimed(star);
            }}
            onClick={() => pick(star)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
}
